import { useState, useEffect, Fragment } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { companySettingsService, type CompanySettings } from '@/services/companySettingsService';

// Helper for currency/percentage display
const formatPercent = (val: number) => `${val.toFixed(2)}%`;
const formatNumber = (val: number) => val.toFixed(4);

export default function CompanySettingsPage() {
    const [settings, setSettings] = useState<CompanySettings[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<CompanySettings | null>(null);

    // Form State
    const [formData, setFormData] = useState<Omit<CompanySettings, 'id'>>({
        regime: null as any,
        rat: 0,
        fap: 1.0,
        thirdParties: 0,
        inssEmployerRate: 20,
        cprbRate: 0,
        startDate: '',
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await companySettingsService.getAll();
            setSettings(data);
        } catch (error) {
            toast.error('Erro ao carregar configurações');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleOpenModal = (item?: CompanySettings) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                regime: item.regime,
                rat: item.rat,
                fap: item.fap,
                thirdParties: item.thirdParties,
                inssEmployerRate: item.inssEmployerRate,
                cprbRate: item.cprbRate,
                startDate: item.startDate,
            });
        } else {
            setEditingItem(null);
            setFormData({
                regime: null as any, // Force selection
                rat: 0,
                fap: 1.0,
                thirdParties: 5.8, // Default common value
                inssEmployerRate: 20.0, // Default common value
                cprbRate: 0, // Default common value
                startDate: new Date().toISOString().slice(0, 7) + '-01', // First day of current month
            });
        }
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Tem certeza que deseja excluir este registro?')) return;
        try {
            await companySettingsService.delete(id);
            toast.success('Registro excluído');
            loadData();
        } catch (error) {
            toast.error('Erro ao excluir');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingItem) {
                await companySettingsService.update(editingItem.id, formData);
                toast.success('Atualizado com sucesso');
            } else {
                await companySettingsService.create(formData);
                toast.success('Criado com sucesso');
            }
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            toast.error('Erro ao salvar');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Configurações da Empresa (Encargos)</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
                >
                    <PlusIcon className="mr-2 h-5 w-5" />
                    Novo Período
                </button>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Vigência</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Regime</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">INSS Patr.</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">RAT x FAP</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Terceiros</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">CPRB (Deson.)</th>
                            <th className="relative px-6 py-3"><span className="sr-only">Ações</span></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {loading ? (
                            <tr><td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">Carregando...</td></tr>
                        ) : settings.length === 0 ? (
                            <tr><td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">Nenhuma configuração encontrada.</td></tr>
                        ) : (
                            settings.map((item) => (
                                <tr key={item.id}>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                                        A partir de {new Date(item.startDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                        {item.regime === 'SIMPLES' ? 'Simples Nacional' : item.regime === 'LUCRO_PRESUMIDO' ? 'Lucro Presumido' : 'Lucro Real'}
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatPercent(item.inssEmployerRate)}</td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatPercent(item.rat)} x {formatNumber(item.fap)}</td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatPercent(item.thirdParties)}</td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{item.cprbRate > 0 ? formatPercent(item.cprbRate) : '—'}</td>
                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                        <button onClick={() => handleOpenModal(item)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900">
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Transition appear show={isModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsModalOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {editingItem ? 'Editar Configuração' : 'Nova Configuração'}
                                    </Dialog.Title>
                                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Início de Vigência</label>
                                            <input
                                                type="date"
                                                required
                                                value={formData.startDate}
                                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Regime Tributário</label>
                                            <select
                                                required
                                                value={formData.regime}
                                                onChange={(e) => setFormData({ ...formData, regime: e.target.value as any })}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            >
                                                <option value="">Selecione...</option>
                                                <option value="SIMPLES">Simples Nacional</option>
                                                <option value="LUCRO_PRESUMIDO">Lucro Presumido</option>
                                                <option value="LUCRO_REAL">Lucro Real</option>
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">INSS Patronal (%)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    required
                                                    value={formData.inssEmployerRate}
                                                    onChange={(e) => setFormData({ ...formData, inssEmployerRate: parseFloat(e.target.value) })}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                                />
                                                <p className="mt-1 text-xs text-gray-500">Padrão 20%. Ajuste conforme regime.</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">CPRB / Desoneração (%)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.cprbRate}
                                                    onChange={(e) => setFormData({ ...formData, cprbRate: parseFloat(e.target.value) })}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                                />
                                                <p className="mt-1 text-xs text-gray-500">Sobre Faturamento. 0 se não se aplica.</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">RAT (%)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    required
                                                    value={formData.rat}
                                                    onChange={(e) => setFormData({ ...formData, rat: parseFloat(e.target.value) })}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">FAP (Multiplicador)</label>
                                                <input
                                                    type="number"
                                                    step="0.0001"
                                                    required
                                                    value={formData.fap}
                                                    onChange={(e) => setFormData({ ...formData, fap: parseFloat(e.target.value) })}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Terceiros / Outras Entidades (%)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                required
                                                value={formData.thirdParties}
                                                onChange={(e) => setFormData({ ...formData, thirdParties: parseFloat(e.target.value) })}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            />
                                        </div>

                                        <div className="mt-6 flex justify-end gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setIsModalOpen(false)}
                                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="submit"
                                                className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
                                            >
                                                Salvar
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}
